// pages/TravelExpense/TravelExpensesPanel.js
//
// Go-live parity (2026-09-24): audit 09-travel #2 — legacy "Expenses
// Incurred" tab of frmTravelExpense.vb. Each traveler records the trip's
// actual costs in the 8 legacy categories, with the payment source
// (Personal Funds / Corporate Card + last 4 / Corporate Cash Advance), a
// comment and an optional receipt (max 30 MB, stored in the database like
// legacy). Lines can be changed or deleted until the traveler presses
// "Submit expenses" (ExpenseEmployee.EmpSubmitted = 1). Accounting
// (travel.accounting) reviews a submitted traveler here and closes the
// reimbursement. A small per-category summary compares the actuals with the
// trip's estimate (legacy Financial Summary, simplified).
//
// Backend (TravelExpenseWorkspaceController):
//   GET    TravelExpense/{id}/expenses?empId=
//   POST   TravelExpense/{id}/expenses/{category}          (multipart)
//   PUT    TravelExpense/{id}/expenses/{category}/{rowId}  (multipart)
//   DELETE TravelExpense/{id}/expenses/{category}/{rowId}
//   GET    TravelExpense/{id}/expenses/{category}/{rowId}/receipt
//   POST   TravelExpense/{id}/expenses/submit
//   POST   TravelExpense/{id}/travelers/{empId}/close-reimbursement

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardBody, Row, Col, Input, Label, Button, Spinner, Badge, Alert } from "reactstrap";
import { ResponsiveTabs } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { toCamel } from "./travelCase";

const MAX_RECEIPT_BYTES = 30000000;   // legacy cap ("exceeds limit of 30 MB")

export const CATEGORIES = [
    { key: "air", label: "Air", estimate: "air" },
    { key: "lodging", label: "Lodging", estimate: "lodging" },
    { key: "meals", label: "Meals", estimate: "meals" },
    { key: "entertainment", label: "Entertainment", estimate: "entertainment" },
    { key: "carRental", label: "Car Rental", estimate: "carRental" },
    { key: "personalCar", label: "Personal Car", estimate: "personalCar" },
    { key: "taxi", label: "Taxi", estimate: "taxi" },
    { key: "misc", label: "Misc", estimate: "misc" },
];

// Legacy form fields per category (frmTravelExpense.Designer.vb).
const FIELDS = {
    air: [
        { key: "date", label: "Departure date / time", type: "datetime" },
        { key: "roundTrip", label: "Round trip", type: "bool" },
        { key: "endDate", label: "Return date / time", type: "datetime", showIf: (f) => f.roundTrip },
        { key: "departLocation", label: "Departure city" },
        { key: "landLocation", label: "Arrival city" },
        { key: "airline", label: "Airline name" },
        { key: "amount", label: "Cost ($)", type: "money" },
    ],
    lodging: [
        { key: "date", label: "Check in", type: "datetime" },
        { key: "endDate", label: "Check out", type: "datetime" },
        { key: "hotelName", label: "Hotel name" },
        { key: "amount", label: "Cost — grand total ($)", type: "money" },
    ],
    meals: [
        { key: "date", label: "Date / time", type: "datetime" },
        { key: "restaurant", label: "Restaurant name" },
        { key: "city", label: "City" },
        { key: "amount", label: "Bill amount ($)", type: "money" },
        { key: "numberOfPeople", label: "# of guests", type: "int" },
        { key: "guestNames", label: "Guest names / Company names", type: "textarea" },
    ],
    entertainment: [
        { key: "date", label: "Date / time", type: "datetime" },
        { key: "eventDescription", label: "Description of the event" },
        { key: "companyName", label: "Company" },
        { key: "amount", label: "Bill amount ($)", type: "money" },
        { key: "numberOfPeople", label: "# of guests", type: "int" },
        { key: "guestNames", label: "Guest names / Company names", type: "textarea" },
    ],
    carRental: [
        { key: "date", label: "Pick-up date / time", type: "datetime" },
        { key: "endDate", label: "Return date / time", type: "datetime" },
        { key: "rentalOutfit", label: "Rental company" },
        { key: "rentalCity", label: "Rental city" },
        { key: "amount", label: "Bill amount ($)", type: "money" },
    ],
    personalCar: [
        { key: "date", label: "Departure date / time", type: "datetime" },
        { key: "fromCity", label: "Departure city" },
        { key: "toCity", label: "Destination" },
        { key: "milesTraveled", label: "Total miles travelled", type: "int" },
        { key: "mileageRate", label: "Rate per mile ($)", type: "money" },
        { key: "tollsAmount", label: "Total tolls ($)", type: "money" },
        { key: "parkingAmount", label: "Total parking ($)", type: "money" },
    ],
    taxi: [
        { key: "date", label: "Date / time", type: "datetime" },
        { key: "fromCity", label: "Departure city" },
        { key: "toCity", label: "Destination city" },
        { key: "amount", label: "Cost ($)", type: "money" },
    ],
    misc: [
        { key: "date", label: "Date / time", type: "datetime" },
        { key: "expenseDetails", label: "Description" },
        { key: "amount", label: "Cost ($)", type: "money" },
    ],
};

const NOTES = {
    carRental: "Add fuel expenses, tolls, and other car-rental incidentals under \"Misc\".",
};

const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const fmtDt = (d) => { if (!d) return "—"; try { return new Date(d).toLocaleString([], { dateStyle: "short", timeStyle: "short" }); } catch { return "—"; } };
const toInputDt = (d) => (d ? String(d).slice(0, 16) : "");
const nowInputDt = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const PAYMENT_LABEL = { PERS: "Personal funds", CC: "Corporate card", ADV: "Cash advance" };

const blankForm = () => ({
    paymentSource: "PERS", cardLastFour: "", comment: "",
    date: nowInputDt(), endDate: nowInputDt(), roundTrip: false,
    departLocation: "", landLocation: "", airline: "", amount: "",
    hotelName: "", restaurant: "", city: "", numberOfPeople: "0", guestNames: "",
    eventDescription: "", companyName: "", rentalCity: "", rentalOutfit: "",
    fromCity: "", toCity: "", milesTraveled: "0", mileageRate: "", tollsAmount: "0", parkingAmount: "0",
    expenseDetails: "",
});

const rowToForm = (r) => ({
    ...blankForm(),
    paymentSource: r.paymentSource || "PERS",
    cardLastFour: r.cardLastFour || "",
    comment: r.comment || "",
    date: toInputDt(r.date) || nowInputDt(),
    endDate: toInputDt(r.endDate) || nowInputDt(),
    roundTrip: !!r.roundTrip,
    departLocation: r.departLocation || "", landLocation: r.landLocation || "", airline: r.airline || "",
    amount: r.amount != null ? String(r.amount) : "",
    hotelName: r.hotelName || "", restaurant: r.restaurant || "", city: r.city || "",
    numberOfPeople: r.numberOfPeople != null ? String(r.numberOfPeople) : "0",
    guestNames: r.guestNames || "", eventDescription: r.eventDescription || "", companyName: r.companyName || "",
    rentalCity: r.rentalCity || "", rentalOutfit: r.rentalOutfit || "",
    fromCity: r.fromCity || "", toCity: r.toCity || "",
    milesTraveled: r.milesTraveled != null ? String(r.milesTraveled) : "0",
    mileageRate: r.mileageRate != null ? String(r.mileageRate) : "",
    tollsAmount: r.tollsAmount != null ? String(r.tollsAmount) : "0",
    parkingAmount: r.parkingAmount != null ? String(r.parkingAmount) : "0",
    expenseDetails: r.expenseDetails || "",
});

// One-line description of a line for the grid.
const describe = (r) => {
    switch (r.category) {
        case "air": return `${r.airline || ""} — ${r.departLocation || ""} → ${r.landLocation || ""}${r.roundTrip ? ` (round trip, return ${fmtDt(r.endDate)})` : ""}`;
        case "lodging": return `${r.hotelName || ""} — ${fmtDt(r.date)} to ${fmtDt(r.endDate)}`;
        case "meals": return `${r.restaurant || ""}, ${r.city || ""}${r.numberOfPeople ? ` — ${r.numberOfPeople} guest(s): ${r.guestNames || ""}` : ""}`;
        case "entertainment": return `${r.eventDescription || ""}${r.companyName ? ` (${r.companyName})` : ""}${r.numberOfPeople ? ` — ${r.numberOfPeople} guest(s): ${r.guestNames || ""}` : ""}`;
        case "carRental": return `${r.rentalOutfit || ""}, ${r.rentalCity || ""} — ${fmtDt(r.date)} to ${fmtDt(r.endDate)}`;
        case "personalCar": return `${r.fromCity || ""} → ${r.toCity || ""} — ${r.milesTraveled ?? 0} mi × ${fmtMoney(r.mileageRate)} + tolls ${fmtMoney(r.tollsAmount)} + parking ${fmtMoney(r.parkingAmount)}`;
        case "taxi": return `${r.fromCity || ""} → ${r.toCity || ""}`;
        case "misc": return r.expenseDetails || "";
        default: return "";
    }
};

const errText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;

const TravelExpensesPanel = ({ tripId, workspace, initialEmpId, onChanged }) => {
    const viewer = workspace?.viewer || {};
    const travelers = useMemo(() => workspace?.travelers || [], [workspace]);
    // A traveler sees their own lines; oversight viewers pick a traveler.
    const selectable = viewer.oversight ? travelers : travelers.filter((t) => t.empid === viewer.empId);
    const defaultEmp = useMemo(() => {
        const wanted = parseInt(initialEmpId, 10);
        if (Number.isFinite(wanted) && selectable.some((t) => t.empid === wanted)) return wanted;
        if (selectable.some((t) => t.empid === viewer.empId)) return viewer.empId;
        return selectable[0]?.empid ?? null;
    }, [initialEmpId, selectable, viewer.empId]);

    const [empId, setEmpId] = useState(defaultEmp);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [category, setCategory] = useState("air");
    const [form, setForm] = useState(blankForm);
    const [editing, setEditing] = useState(null);     // row being edited
    const [file, setFile] = useState(null);
    const [removeReceipt, setRemoveReceipt] = useState(false);
    const [busy, setBusy] = useState(false);
    const [fileKey, setFileKey] = useState(0);

    useEffect(() => { setEmpId(defaultEmp); }, [defaultEmp]);

    const load = useCallback(() => {
        if (empId == null) return;
        setLoading(true); setError(null);
        api.get(`TravelExpense/${tripId}/expenses`, { params: { empId } })
            .then(({ data: raw }) => setData(toCamel(raw)))
            .catch((e) => setError(errText(e, "Failed to load expenses")))
            .finally(() => setLoading(false));
    }, [tripId, empId]);
    useEffect(load, [load]);

    const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data]);
    const canEdit = !!data?.canEdit;

    const summary = useMemo(() => {
        const byCat = {};
        const bySource = { PERS: 0, CC: 0, ADV: 0 };
        CATEGORIES.forEach((c) => { byCat[c.key] = 0; });
        items.forEach((r) => {
            const cost = Number(r.cost) || 0;
            byCat[r.category] = (byCat[r.category] || 0) + cost;
            if (bySource[r.paymentSource] != null) bySource[r.paymentSource] += cost;
        });
        const total = Object.values(byCat).reduce((a, b) => a + b, 0);
        const est = data?.estimate || {};
        const estTotal = CATEGORIES.reduce((a, c) => a + (Number(est[c.estimate]) || 0), 0);
        return { byCat, bySource, total, est, estTotal };
    }, [items, data]);

    const resetForm = () => {
        setForm(blankForm()); setEditing(null); setFile(null); setRemoveReceipt(false); setFileKey((k) => k + 1);
    };

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setError(null); setNotice(null);
        if (file && file.size > MAX_RECEIPT_BYTES) {
            setError("Error, file exceeds limit of 30 MB. Please try your operation again.");
            return;
        }
        const fd = new FormData();
        const fields = FIELDS[category].filter((f) => !f.showIf || f.showIf(form));
        fd.append("paymentSource", form.paymentSource);
        if (form.paymentSource === "CC") fd.append("cardLastFour", form.cardLastFour.trim());
        fd.append("comment", form.comment || "");
        fields.forEach((f) => {
            const v = form[f.key];
            if (f.type === "bool") fd.append(f.key, v ? "true" : "false");
            else if (f.type === "datetime") { if (v) fd.append(f.key, `${v}:00`); }
            else if (v !== "" && v != null) fd.append(f.key, String(v).trim());
        });
        if (file) fd.append("file", file);
        if (editing && removeReceipt && !file) fd.append("removeAttachment", "true");

        setBusy(true);
        try {
            const cfg = { headers: { "Content-Type": undefined }, maxBodyLength: 50 * 1024 * 1024, maxContentLength: 50 * 1024 * 1024 };
            if (editing) await api.put(`TravelExpense/${tripId}/expenses/${category}/${editing.id}`, fd, cfg);
            else await api.post(`TravelExpense/${tripId}/expenses/${category}`, fd, cfg);
            setNotice(editing ? "Expense line updated." : "Expense line added.");
            resetForm();
            load();
        } catch (e) {
            setError(errText(e, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (r) => {
        if (!window.confirm("Are you sure you wish to delete this expense line?")) return;
        setBusy(true); setError(null); setNotice(null);
        try {
            await api.delete(`TravelExpense/${tripId}/expenses/${r.category}/${r.id}`);
            if (editing && editing.id === r.id && editing.category === r.category) resetForm();
            load();
        } catch (e) { setError(errText(e, "Delete failed")); }
        finally { setBusy(false); }
    };

    const downloadReceipt = async (r) => {
        try {
            const res = await api.get(`TravelExpense/${tripId}/expenses/${r.category}/${r.id}/receipt`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = r.attachmentFilename || "receipt";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) { setError("The receipt could not be downloaded."); }
    };

    const submitExpenses = async () => {
        const ok = window.confirm("Please confirm you have completely submitted all expenses for your travel. "
            + "Additional reimbursements past this point will not be possible. If you are not ready to finalize "
            + "your report, you may leave this page and all entries stay saved. Are you ready to finalize your submission?");
        if (!ok) return;
        setBusy(true); setError(null); setNotice(null);
        try {
            await api.post(`TravelExpense/${tripId}/expenses/submit`);
            setNotice("Your expenses were submitted to Accounting.");
            resetForm();
            load();
            onChanged && onChanged();
        } catch (e) { setError(errText(e, "Submit failed")); }
        finally { setBusy(false); }
    };

    const closeReimbursement = async () => {
        if (!window.confirm("Close out the reimbursement for this employee? The trip leaves the Awaiting Reimbursement queue.")) return;
        setBusy(true); setError(null); setNotice(null);
        try {
            await api.post(`TravelExpense/${tripId}/travelers/${empId}/close-reimbursement`);
            setNotice("Reimbursement closed.");
            load();
            onChanged && onChanged();
        } catch (e) { setError(errText(e, "Close failed")); }
        finally { setBusy(false); }
    };

    const startEdit = (r) => {
        setCategory(r.category);
        setEditing(r);
        setForm(rowToForm(r));
        setFile(null); setRemoveReceipt(false); setFileKey((k) => k + 1);
        setNotice(null); setError(null);
    };

    if (empId == null) return <Alert color="info" className="mb-0">There are no travelers on this trip.</Alert>;

    const traveler = data?.traveler;
    const catRows = items.filter((r) => r.category === category);
    const tabs = CATEGORIES.map((c) => ({ key: c.key, label: c.label, count: items.filter((r) => r.category === c.key).length }));

    const renderField = (f) => {
        if (f.showIf && !f.showIf(form)) return null;
        const v = form[f.key];
        if (f.type === "bool") {
            return (
                <Col md={4} key={f.key} className="d-flex align-items-end">
                    <div className="form-check mb-2">
                        <Input type="checkbox" id={`te-${f.key}`} className="form-check-input" checked={!!v}
                            onChange={(e) => setField(f.key, e.target.checked)} disabled={busy} />
                        <Label for={`te-${f.key}`} className="form-check-label">{f.label}</Label>
                    </div>
                </Col>
            );
        }
        return (
            <Col md={f.type === "textarea" ? 12 : 4} key={f.key}>
                <Label className="mb-1">{f.label}</Label>
                <Input
                    type={f.type === "datetime" ? "datetime-local" : f.type === "textarea" ? "textarea" : f.type === "int" || f.type === "money" ? "number" : "text"}
                    step={f.type === "money" ? "0.01" : f.type === "int" ? "1" : undefined}
                    min={f.type === "money" || f.type === "int" ? "0" : undefined}
                    rows={f.type === "textarea" ? 2 : undefined}
                    maxLength={f.type === "text" || !f.type ? 500 : undefined}
                    value={v ?? ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    disabled={busy} />
            </Col>
        );
    };

    return (
        <div>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                {selectable.length > 1 ? (
                    <div style={{ minWidth: 240 }}>
                        <Input type="select" bsSize="sm" value={empId ?? ""} onChange={(e) => { setEmpId(parseInt(e.target.value, 10)); resetForm(); }}>
                            {selectable.map((t) => <option key={t.empid} value={t.empid}>{t.name}</option>)}
                        </Input>
                    </div>
                ) : (
                    <strong>{traveler?.name || ""}</strong>
                )}
                {traveler && (traveler.reimbursementClosed
                    ? <Badge color="dark" pill>Reimbursement closed</Badge>
                    : traveler.empSubmitted
                        ? <Badge color="success" pill>Expenses submitted</Badge>
                        : <Badge color="warning" pill>Not submitted yet</Badge>)}
                <div className="ms-auto d-flex gap-2">
                    {canEdit && (
                        <Button color="success" size="sm" disabled={busy} onClick={submitExpenses}>Submit expenses</Button>
                    )}
                    {viewer.isAccounting && traveler?.empSubmitted && !traveler?.reimbursementClosed && (
                        <Button color="primary" size="sm" disabled={busy} onClick={closeReimbursement}>Close reimbursement for this employee</Button>
                    )}
                </div>
            </div>

            {error && <Alert color="danger">{error}</Alert>}
            {notice && <Alert color="success">{notice}</Alert>}
            {canEdit && (
                <Alert color="info" className="py-2 small">
                    Enter every expense you incurred during the trip. Include any expense covered by a cash advance;
                    exclude anything HR prepurchased. When everything is entered, press <b>Submit expenses</b> —
                    after that no more changes are possible.
                </Alert>
            )}

            {loading && !data ? (
                <div className="text-center py-4"><Spinner color="primary" /></div>
            ) : (
                <>
                    <ResponsiveTabs tabs={tabs} activeKey={category} size="sm"
                        onChange={(k) => { setCategory(k); if (editing) resetForm(); }} />
                    {NOTES[category] && <div className="text-muted small mb-2">{NOTES[category]}</div>}

                    {catRows.length === 0 ? (
                        <div className="text-muted mb-3">No {CATEGORIES.find((c) => c.key === category)?.label.toLowerCase()} expenses.</div>
                    ) : (
                        <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: 140 }}>Date</th>
                                        <th>Details</th>
                                        <th style={{ width: 150 }}>Payment</th>
                                        <th className="text-end" style={{ width: 110 }}>Cost</th>
                                        <th>Comment</th>
                                        <th style={{ width: 110 }}>Receipt</th>
                                        {canEdit && <th style={{ width: 110 }}></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {catRows.map((r) => (
                                        <tr key={`${r.category}-${r.id}`} className={r.reimbursementId ? "table-success" : undefined}>
                                            <td>{fmtDt(r.date)}</td>
                                            <td>
                                                {describe(r)}
                                                {r.group && <Badge color="info" className="ms-1">group — split per traveler</Badge>}
                                            </td>
                                            <td>{PAYMENT_LABEL[r.paymentSource] || r.paymentSource}{r.paymentSource === "CC" && r.cardLastFour ? ` …${r.cardLastFour}` : ""}</td>
                                            <td className="text-end">{fmtMoney(r.cost)}</td>
                                            <td className="small">{r.comment || ""}</td>
                                            <td>
                                                {r.hasAttachment
                                                    ? <Button color="link" size="sm" className="p-0" onClick={() => downloadReceipt(r)} title={r.attachmentFilename || ""}>Download</Button>
                                                    : <span className="text-muted small">—</span>}
                                            </td>
                                            {canEdit && (
                                                <td>
                                                    {!r.group && !r.reimbursementId && (
                                                        <>
                                                            <Button color="link" size="sm" className="p-0 me-2" disabled={busy} onClick={() => startEdit(r)}>Edit</Button>
                                                            <Button color="link" size="sm" className="p-0 text-danger" disabled={busy} onClick={() => remove(r)}>Delete</Button>
                                                        </>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {canEdit && (
                        <Card className="border mb-3">
                            <CardBody>
                                <h6 className="mb-3">
                                    {editing ? `Change ${CATEGORIES.find((c) => c.key === category)?.label} expense` : `Add new ${CATEGORIES.find((c) => c.key === category)?.label} expense`}
                                </h6>
                                <Row className="g-2">
                                    {FIELDS[category].map(renderField)}
                                    <Col md={12}>
                                        <Label className="mb-1 d-block">Payment source</Label>
                                        {[["PERS", "Personal Funds"], ["CC", "Corporate Card"], ["ADV", "Corporate Cash Advance"]].map(([k, label]) => (
                                            <div className="form-check form-check-inline" key={k}>
                                                <Input type="radio" className="form-check-input" id={`te-src-${k}`} name="te-src"
                                                    checked={form.paymentSource === k} onChange={() => setField("paymentSource", k)} disabled={busy} />
                                                <Label className="form-check-label" for={`te-src-${k}`}>{label}</Label>
                                            </div>
                                        ))}
                                        {form.paymentSource === "CC" && (
                                            <Input bsSize="sm" className="d-inline-block ms-2" style={{ width: 110 }} maxLength={4}
                                                placeholder="last 4 digits" value={form.cardLastFour}
                                                onChange={(e) => setField("cardLastFour", e.target.value.replace(/\D/g, ""))} disabled={busy} />
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        <Label className="mb-1">Comment</Label>
                                        <Input value={form.comment} maxLength={1000} onChange={(e) => setField("comment", e.target.value)} disabled={busy} />
                                    </Col>
                                    <Col md={6}>
                                        <Label className="mb-1">Receipt (optional, max 30 MB)</Label>
                                        <Input key={fileKey} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy} />
                                        {editing?.hasAttachment && !file && (
                                            <div className="form-check mt-1">
                                                <Input type="checkbox" className="form-check-input" id="te-rm" checked={removeReceipt}
                                                    onChange={(e) => setRemoveReceipt(e.target.checked)} disabled={busy} />
                                                <Label className="form-check-label small" for="te-rm">Remove the current receipt ({editing.attachmentFilename || "file"})</Label>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                                <div className="d-flex gap-2 mt-3">
                                    <Button color="primary" disabled={busy} onClick={save}>
                                        {busy ? <Spinner size="sm" /> : editing ? "Save changes" : "Add expense"}
                                    </Button>
                                    {editing && <Button color="link" disabled={busy} onClick={resetForm}>Cancel edit</Button>}
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    <h6 className="mt-2">Summary — {traveler?.name || ""}</h6>
                    <div className="table-responsive">
                        <table className="table table-sm mb-1" style={{ maxWidth: 620 }}>
                            <thead className="table-light">
                                <tr><th>Category</th><th className="text-end">Estimated (whole group)</th><th className="text-end">Actual (this traveler)</th></tr>
                            </thead>
                            <tbody>
                                {CATEGORIES.map((c) => (
                                    <tr key={c.key}>
                                        <td>{c.label}</td>
                                        <td className="text-end">{fmtMoney(summary.est[c.estimate] ?? 0)}</td>
                                        <td className="text-end">{fmtMoney(summary.byCat[c.key] || 0)}</td>
                                    </tr>
                                ))}
                                <tr className="table-light fw-bold">
                                    <td>Total</td>
                                    <td className="text-end">{fmtMoney(summary.estTotal)}</td>
                                    <td className="text-end">{fmtMoney(summary.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="small text-muted">
                        Paid with personal funds: <b>{fmtMoney(summary.bySource.PERS)}</b> ·
                        corporate card: <b>{fmtMoney(summary.bySource.CC)}</b> ·
                        cash advance: <b>{fmtMoney(summary.bySource.ADV)}</b>
                        {(data?.travelerCount ?? 1) > 1 && <> · group items are divided by {data.travelerCount} travelers</>}
                    </div>
                </>
            )}
        </div>
    );
};

export default TravelExpensesPanel;
